<template>
  <section id="sl-catalog" class="master-section">
    <header>
      <h1 class="title">All Items</h1>
      <router-link :to="{name: 'item-create', params: {fromRouteName: 'catalog'}}" class="btn btn-primary"><fa-icon icon="plus-circle"></fa-icon></router-link>
    </header>
    <template v-if="categoriesAreLoaded && itemsAreLoaded && storesAreLoaded">
      <div class="sl-category-picker show-sm">
        <div class="input-group">
          <button class="btn btn-primary input-group-btn" @click.stop="prevCategory"><fa-icon icon="arrow-left"></fa-icon></button>
          <select class="form-select" v-model="visibleCategory">
            <option v-for="cat in categories" :key="cat.id" :value="cat">{{cat.name}}</option>
          </select>
          <button class="btn btn-primary input-group-btn" @click.stop="nextCategory"><fa-icon icon="arrow-right"></fa-icon></button>
        </div>
      </div>
      <div class="columns">
        <div class="column col-3 hide-sm">
          <ul class="menu sl-menu">
            <li class="divider" data-content="Categories"></li>
            <li class="menu-item" v-for="category in categories" :key="category.id">
              <a href="#" @click.prevent="viewCategory(category)" :class="{'active': visibleCategory && category.id === visibleCategory.id}">
                {{ category.name }}
              </a>
            </li>
          </ul>
        </div>
        <div class="column col-9 col-sm-12">
          <section class="sl-category" v-if="this.visibleCategory">
            <div class="title h3 hide-sm">{{this.visibleCategory.name}} Items</div>
            <div class="items">
              <div class="item" v-for="item in categoryItems" :key="item.id">
                <div class="cart-control">
                  <template v-if="itemBusy(item)">
                    <fa-icon icon="cog" :spin="true" />
                  </template>
                  <template v-else>
                    <div class="is-needed" v-if="item.quantityNeeded > 0">
                      <fa-icon icon="shopping-basket"></fa-icon>
                    </div>
                    <div class="not-needed" v-else>
                      <button class="btn" @click.prevent="addToList(item)"><fa-icon icon="plus-circle"></fa-icon></button>
                    </div>
                  </template>
                </div>
                <div class="details">
                  <div class="name">
                    <router-link :to="{name: 'item-edit', params: {id: `${item.id}`, fromRouteName: 'catalog'}}" class="name">{{ item.name }}</router-link>
                    <span v-if="item.isEventualPurchase" class="status-icon" title="Purchase eventually">&nbsp;<fa-icon :icon="['far', 'calendar-alt']" /></span>
                    <span v-if="item.isOneTimeItem" class="status-icon" title="One-time purchase">&nbsp;<fa-icon icon="backspace" /></span>
                  </div>
                  <div class="notes">{{ item.notes }}</div>
                  <div class="stores" v-if="item.storeIds.length">
                    <span v-for="s in itemStores(item)" :key="s.id" class="label label-rounded sl-store-label">{{ s.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="loading loading-xl"></div>
    </template>
  </section>
</template>

<script>
import _ from 'lodash'

export default {
  name: 'catalog',
  data () {
    return {
      visibleCategory: null,
      busyMap: {}
    }
  },
  computed: {
    categoryItems () {
      return _.sortBy(this.visibleCategory ? this.itemsByCategoryId[this.visibleCategory.id] : [], m => m.name.toLowerCase())
    }
  },
  methods: {
    viewCategory (cat) {
      this.visibleCategory = cat
    },
    pickDefaultCategory () {
      if (this.categoriesAreLoaded && !this.visibleCategory) {
        this.visibleCategory = this.categories[0]
      }
    },
    itemStores (item) {
      return _.map(item.storeIds, sId => this.getStoreForId(sId))
    },
    itemBusy (item) {
      return this.busyMap[item.id]
    },
    async addToList (item) {
      this.$set(this.busyMap, item.id, true)
      await this.setItemQuantityNeeded({ id: item.id, quantityNeeded: item.quantityNeeded + 1 })
      this.$set(this.busyMap, item.id, false)
    },
    nextCategory () {
      if (!this.visibleCategory) {
        return
      }
      const curIdx = this.categories.indexOf(this.visibleCategory)
      let nextIdx = curIdx + 1
      if (nextIdx === this.categories.length) {
        nextIdx = 0
      }
      this.visibleCategory = this.categories[nextIdx]
    },
    prevCategory () {
      if (!this.visibleCategory) {
        return
      }
      const curIdx = this.categories.indexOf(this.visibleCategory)
      let prevIdx = curIdx - 1
      if (prevIdx < 0) {
        prevIdx = this.categories.length - 1
      }
      this.visibleCategory = this.categories[prevIdx]
    }
  },
  watch: {
    categoriesAreLoaded (loaded) {
      if (loaded) {
        this.pickDefaultCategory()
      }
    }
  },
  created () {
    this.verifySignedIn()
  },
  mounted () {
    this.pickDefaultCategory()
  },
  beforeRouteEnter (to, from, next) {
    next(self => {
      self.setPageMeta(to.meta)
    })
  }
}
</script>

<style lang="scss">
  @import '../assets/_vars';
  #sl-catalog {
    .sl-category-picker {
      margin-bottom: 1rem;
    }

    .sl-category {
      .title {
        line-height: 1.8em;
      }

      .items {
        .item {
          display: flex;
          width: 100%;
          min-height: 2.25rem;
          align-items: center;
          margin: 0.25rem 0;
          padding: 0;
          background-color: darken($dark-color, 4%);

          &:first-of-type {
            margin-top: 0;
          }
          &:last-of-type {
            margin-bottom: 0;
          }

          .cart-control {
            flex: 1 0 2.5rem;
            display: flex;
            align-items: center;
            justify-content: center;

            .is-needed {
              color: $darkest-color;
            }
            .not-needed {
              color: $light-color;
              .btn {
                width: 1.8rem;
                height: 1.8rem;
                border-radius: 50%;
              }
            }
          }
          .details {
            flex: 1 1 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: flex-start;
            padding: 0.25rem 0;

            .name {
              line-height: 1.1em;

              .status-icon {
                display: inline-block;
                margin-left: 0.25rem;
                color: lighten($primary-color, 10%);
              }
            }

            .notes {
              font-size: 0.8rem;
              line-height: 1rem;
              font-style: italic;
              color: lighten($dark-color, 20%);
            }

            .stores {
              padding-top: 0.25rem;
              .sl-store-label + .sl-store-label {
                margin-left: 0.25rem;
              }
            }
          }
        }
      }
    }

    .sl-menu {
      background-color: $darkest-color;

      li {
        &.divider{
          border-color: $primary-color;
          &::after {
            color: $primary-color;
            background-color: $darkest-color;
          }
        }

        &.menu-item {
          a {
            &:hover, &.active {
              background-color: $dark-color;
              color: $light-color;
            }
          }
        }
      }
    }
  }
</style>
